import { validators } from './validators';
import { Validators } from './types';
import priority from './priority';

const validatorKeys = Object.keys(validators);

export type UserValidators = {
  [key in Validators]?:
    | boolean
    | string
    | {
        message: string;
        [k: string]: any;
      };
};

export type BuilderValidationResult = {
  valid: boolean;
  results: Array<{
    type: Validators;
    valid: boolean;
    message: string;
  }>;
};

export function createBuilder(userValidators: UserValidators) {
  const validatorKeysProvided = Object.keys(userValidators);

  if (validatorKeysProvided.length === 0) {
    throw new Error(
      'createBuilder needs to be passed an array with at least one validator'
    );
  }

  return (input: string): BuilderValidationResult => {
    let totalValid = true;

    let results = validatorKeysProvided
      .map((validatorKey: Validators) => {
        const validatorConfig = userValidators[validatorKey];

        if (!isValidatorKey(validatorKey)) {
          throw new Error(
            `createBuilder doesn't recognize the validator "${validatorKey}"`
          );
        }

        const [valid, defaultMessage] = validators[validatorKey](input);

        if (valid) {
          return null;
        }

        totalValid = false;
        let message = defaultMessage;

        if (typeof validatorConfig === 'string') {
          message = validatorConfig;
        } else if (typeof validatorConfig === 'object') {
          message = validatorConfig.message;
        }

        return {
          type: validatorKey,
          valid,
          message,
        };
      })
      .filter(item => item !== null)
      .sort((prev, next) => priority[prev.type] - priority[next.type]);

    // If 'isRequired' is not present, and if input is empty, we can safely
    // ignore reset of the validator results
    if (
      results.length &&
      results[0].type !== 'isRequired' &&
      input.length === 0
    ) {
      results = [];
      totalValid = true;
    }

    return { valid: totalValid, results };
  };
}

function isValidatorKey(key: any): key is Validators {
  return validatorKeys.includes(key);
}
